import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/app/Layout";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Heart, Share2, MessageCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { catName } from "@/lib/categories";
import ProjectCard from "@/components/app/ProjectCard";
import { SaveButton } from "@/components/app/SaveButton";

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [related, setRelated] = useState([]);
  const [newComment, setNewComment] = useState("");
  const { user } = useAuth();

  const load = async () => {
    const [p, c, r] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/comments`),
      api.get(`/projects/${id}/related`),
    ]);
    setProject(p.data); setComments(c.data); setRelated(r.data);
  };

  useEffect(() => { load(); }, [id]);

  const toggleLike = async () => {
    if (!user) return toast.error("Login to like projects");
    const { data } = await api.post(`/projects/${id}/like`);
    setProject({ ...project, liked_by_me: data.liked, like_count: data.like_count });
  };

  const share = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Login to comment");
    if (!newComment.trim()) return;
    await api.post(`/projects/${id}/comments`, { content: newComment });
    setNewComment("");
    const c = await api.get(`/projects/${id}/comments`);
    setComments(c.data);
  };

  if (!project) return <Layout><div className="py-24 text-center text-muted-foreground">Loading…</div></Layout>;

  const creator = project.creator || {};

  return (
    <Layout>
      <article className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="meta-label mb-3">{catName(project.category)}</div>
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1]" data-testid="project-detail-title">{project.title}</h1>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-border/60">
          <Link to={`/u/${creator.username}`} className="flex items-center gap-3 group">
            <Avatar className="h-10 w-10"><AvatarImage src={creator.avatar_url} /><AvatarFallback>{creator.full_name?.[0]}</AvatarFallback></Avatar>
            <div>
              <div className="font-semibold group-hover:text-[hsl(var(--accent))]">{creator.full_name}</div>
              <div className="text-xs text-muted-foreground">@{creator.username} · {creator.profession}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-3 meta-label mr-2">
              <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {project.views}</span>
              <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {project.comment_count}</span>
            </div>
            <Button variant={project.liked_by_me ? "default" : "outline"} onClick={toggleLike} className={project.liked_by_me ? "bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white" : ""} data-testid="like-btn">
              <Heart className={`h-4 w-4 mr-2 ${project.liked_by_me ? "fill-current" : ""}`} /> {project.like_count}
            </Button>
            <SaveButton projectId={project.project_id} variant="button" />
            <Button variant="outline" onClick={share} data-testid="share-btn"><Share2 className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          {project.media?.map((m, i) => (
            m.type === "video" ? (
              <video key={i} src={mediaUrl(m.url)} controls className="w-full rounded-lg bg-black" />
            ) : (
              <img key={i} src={mediaUrl(m.url)} alt={`${project.title} ${i + 1}`} className="w-full rounded-lg" loading="lazy" />
            )
          ))}
        </div>

        {project.description && (
          <div className="mt-10 max-w-3xl text-base sm:text-lg leading-relaxed text-muted-foreground whitespace-pre-wrap">{project.description}</div>
        )}

        <div className="mt-10 flex flex-wrap gap-6 text-sm border-t border-border/60 pt-6">
          <div>
            <div className="meta-label mb-2">Software</div>
            <div className="flex flex-wrap gap-2">{project.software?.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}</div>
          </div>
          <div>
            <div className="meta-label mb-2">Tags</div>
            <div className="flex flex-wrap gap-2">{project.tags?.map((t) => <Badge key={t} variant="outline">#{t}</Badge>)}</div>
          </div>
          {project.credits && (
            <div>
              <div className="meta-label mb-2">Credits</div>
              <div className="whitespace-pre-wrap">{project.credits}</div>
            </div>
          )}
        </div>

        {/* Comments */}
        <section className="mt-14">
          <h3 className="font-display text-2xl font-bold">Comments ({comments.length})</h3>
          <form onSubmit={postComment} className="mt-6 flex gap-2">
            <input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={user ? "Share your thoughts…" : "Log in to comment"}
              disabled={!user}
              className="flex-1 bg-secondary px-4 py-3 rounded-md text-sm"
              data-testid="comment-input"
            />
            <Button type="submit" disabled={!user || !newComment.trim()} className="bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white" data-testid="comment-submit">Post</Button>
          </form>
          <div className="mt-6 space-y-5">
            {comments.map((c) => (
              <div key={c.comment_id} className="flex gap-3" data-testid={`comment-${c.comment_id}`}>
                <Avatar className="h-9 w-9"><AvatarImage src={c.user?.avatar_url} /><AvatarFallback>{c.user?.username?.[0]}</AvatarFallback></Avatar>
                <div>
                  <div className="text-sm"><span className="font-semibold">@{c.user?.username}</span> <span className="meta-label ml-2">{new Date(c.created_at).toLocaleDateString()}</span></div>
                  <div className="text-sm mt-1 text-muted-foreground">{c.content}</div>
                </div>
              </div>
            ))}
            {comments.length === 0 && <div className="text-sm text-muted-foreground">Be the first to comment.</div>}
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-16 border-t border-border/60 pt-10">
            <h3 className="font-display text-2xl font-bold mb-6">More from @{creator.username}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p) => <ProjectCard key={p.project_id} project={p} />)}
            </div>
          </section>
        )}
      </article>
    </Layout>
  );
}
